import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { api } from '../lib/api';
import { notify } from '../lib/notify';
import TaleStoryPanel, { type TaleDetail } from '../components/tales/TaleStoryPanel';
import ImageGrid from '../components/images/ImageGrid';
import TaleThumbnailsTab from '../components/thumbnails/TaleThumbnailsTab';

const TABS = ['Story','Images','Thumbnails','Audio','Storyboard','YouTube','Status'] as const;

type Tale = {
  id: string;
  title: string;
  status: string;
  verse_ref: string | null;
  createdAtMs: number;
  updatedAtMs: number;
  content?: any;
};

export default function TaleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get tab from URL query parameter, default to 'Story'
  const getTabFromURL = () => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      // Find matching tab (case-insensitive)
      const matchedTab = TABS.find(t => t.toLowerCase() === tabParam.toLowerCase());
      if (matchedTab) {
        return matchedTab;
      }
    }
    return 'Story';
  };
  
  const [tab, setTab] = useState<typeof TABS[number]>(getTabFromURL());
  
  const [tale, setTale] = useState<Tale | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  
  // Update tab when URL changes
  useEffect(() => {
    setTab(getTabFromURL());
  }, [location.search]);
  
  // Update URL when tab changes
  const handleTabChange = (newTab: typeof TABS[number]) => {
    setTab(newTab);
    if (id) {
      navigate(`/tales/${id}?tab=${newTab.toLowerCase()}`, { replace: true });
    }
  };
  
  useEffect(() => {
    let alive = true;
    setLoading(true);
    
    if (!id) return;

    api(`/api/tales/${id}/detail`)
      .then(data => { 
        if (alive) { 
          setTale(data); 
          setErr(null); 
        } 
      })
      .catch(async (e: any) => {
        const msg = e?.message || 'Failed to load tale';
        if (alive) setErr(msg);
        notify.error('Failed to load tale', msg);
      })
      .finally(() => alive && setLoading(false));
      
    return () => { alive = false; };
  }, [id]);
  
  if (!id) return null;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading tale details...</p>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center text-red-400">
          <h3 className="text-lg font-semibold mb-2">Error loading tale</h3>
          <p>{err}</p>
        </div>
      </div>
    );
  }

  if (!tale) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center text-slate-400">
          <h3 className="text-lg font-semibold mb-2">Tale not found</h3>
          <p>The tale you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
            {/* Tale Header */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-slate-700">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              {tale.title || 'Untitled tale'}
            </h1>
            <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
              {tale.verse_ref ? (
                <span>📖 {tale.verse_ref}</span>
              ) : (
                <span>📖 (no verse)</span>
              )}
              <span>🎯 {tale.status}</span>
              <span>📅 {new Date(tale.createdAtMs).toLocaleString()}</span>
              {tale.updatedAtMs !== tale.createdAtMs && (
                <span>✏️ {new Date(tale.updatedAtMs).toLocaleString()}</span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <button 
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              onClick={() => {
                api(`/api/tales/${tale.id}/images`, { method: 'POST' })
                  .then(() => notify.success('Images queued', 'Image generation started'))
                  .catch((e: any) => notify.error('Failed to queue images', e?.message));
              }}
            >
              Generate Images
            </button>
            <button 
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              onClick={() => {
                api(`/api/tales/${tale.id}/package`, { method: 'POST' })
                  .then(() => notify.success('Packaging started', 'Video packaging queued'))
                  .catch((e: any) => notify.error('Failed to start packaging', e?.message));
              }}
            >
              Package Video
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700">
        <div className="border-b border-gray-200 dark:border-slate-700 p-2">
          <div className="flex flex-wrap gap-2">
            {TABS.map(t => (
              <button 
                key={t} 
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  tab === t 
                    ? 'bg-blue-500 text-white' 
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`} 
                onClick={() => handleTabChange(t)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        
        <div className="p-6">
          {tab === 'Story' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Story</h3>
              <TaleStoryPanel tale={tale as TaleDetail} />
            </div>
          )}
          
          {tab === 'Status' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Tale Status</h3>
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <h4 className="font-medium mb-2">Next Steps</h4>
                    <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                      <li>• Generate images for your tale</li>
                      <li>• Create storyboard and audio</li>
                      <li>• Package into video</li>
                      <li>• Upload to YouTube</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <h4 className="font-medium mb-2">Tale Information</h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="text-slate-500">ID:</span> {tale.id}</div>
                      <div><span className="text-slate-500">Status:</span> <span className="text-green-600">{tale.status}</span></div>
                      <div><span className="text-slate-500">Created:</span> {new Date(tale.createdAtMs).toLocaleString()}</div>
                      {tale.updatedAtMs !== tale.createdAtMs && (
                        <div><span className="text-slate-500">Updated:</span> {new Date(tale.updatedAtMs).toLocaleString()}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {tab === 'Images' && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Images for {tale.title}</h3>
              <ImageGrid taleId={tale.id} />
            </div>
          )}
          
          {tab === 'Thumbnails' && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Thumbnails for {tale.title}</h3>
              <TaleThumbnailsTab taleId={tale.id} />
            </div>
          )}
          
          {tab === 'Audio' && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Audio for {tale.title}</h3>
              <div className="text-slate-500">AudioPanel component will be implemented here for tale {tale.id}</div>
            </div>
          )}
          
          {tab === 'Storyboard' && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Storyboard for {tale.title}</h3>
              <div className="text-slate-500">StoryboardEditor component will be implemented here for tale {tale.id}</div>
            </div>
          )}
          
          {tab === 'YouTube' && (
            <div>
              <h3 className="text-lg font-semibold mb-3">YouTube for {tale.title}</h3>
              <div className="text-slate-500">YoutubePanel component will be implemented here for tale {tale.id}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
